import moment from 'moment';
import type { ISupportCategory } from '../../../../models/Support/ISupportCategory';
import type {
	ISupportReportAgent,
	ISupportReportAssistant,
	ISupportReportCategoryCount,
	ISupportReportDayCount,
	ISupportReportFirstResponseBucket,
	ISupportReportOverview,
	SupportReportFirstResponseBucketKey,
} from '../../../../models/Support/ISupportReportOverview';
import { THEME } from '../../../../web/components-v2/Theme/theme';

/** The format the range bounds take in the URL and in the DateRangePicker. */
export const REPORT_DATE_FORMAT = 'YYYY-MM-DD';

/** Span shown when the URL carries no range, or only one of the two bounds. */
export const REPORT_DEFAULT_RANGE_DAYS = 30;

/** Mirrors SupportReportingService.MaxRangeDays, so a longer pick is refused before it is requested. */
export const REPORT_RANGE_DAYS_MAX = 400;

/** Both bounds are inclusive calendar days in the viewer's timezone. */
export interface ReportDateRange {
	startDate: string;
	endDate: string;
}

export interface ReportWindow {
	start: string;
	end: string;
}

function parseReportDate(value: string): moment.Moment | null {
	const parsed = moment(value, REPORT_DATE_FORMAT, true);
	return parsed.isValid() ? parsed : null;
}

/** The trailing 30 days ending today, inclusive of today. */
export function getDefaultReportDateRange(now: Date = new Date()): ReportDateRange {
	const end = moment(now).startOf('day');
	return {
		startDate: end
			.clone()
			.subtract(REPORT_DEFAULT_RANGE_DAYS - 1, 'days')
			.format(REPORT_DATE_FORMAT),
		endDate: end.format(REPORT_DATE_FORMAT),
	};
}

/** Whole calendar days covered by the range, both bounds included; null when either bound is unusable. */
export function getReportRangeDayCount(range: ReportDateRange): number | null {
	const start = parseReportDate(range.startDate);
	const end = parseReportDate(range.endDate);
	if (!start || !end) return null;
	return end.diff(start, 'days') + 1;
}

/**
 * The reason the range cannot be reported on, for display in place of the report; null when it can.
 * Checking here keeps a bad range — which the URL can carry — from becoming a request the API
 * rejects with a message the screen would only surface as a generic failure.
 */
export function getReportRangeError(range: ReportDateRange): string | null {
	const dayCount = getReportRangeDayCount(range);
	if (dayCount == null) return 'Pick a start and end date.';
	if (dayCount < 1) return 'The end date must be on or after the start date.';
	if (dayCount > REPORT_RANGE_DAYS_MAX) return `Pick a range of ${REPORT_RANGE_DAYS_MAX} days or fewer.`;
	return null;
}

/**
 * The `[start, end)` instants the API takes: local midnight of the first day through local midnight
 * after the last, so the final day is always fully included. Null when a bound is unusable.
 */
export function getReportWindow(range: ReportDateRange): ReportWindow | null {
	const start = parseReportDate(range.startDate);
	const end = parseReportDate(range.endDate);
	if (!start || !end) return null;
	return { start: start.toISOString(), end: end.add(1, 'day').toISOString() };
}

export function formatReportRangeLabel(range: ReportDateRange): string {
	const start = parseReportDate(range.startDate);
	const end = parseReportDate(range.endDate);
	if (!start || !end) return 'Select a date range';
	if (start.isSame(end, 'day')) return start.format('MMM D, YYYY');
	return `${start.format(start.isSame(end, 'year') ? 'MMM D' : 'MMM D, YYYY')} – ${end.format('MMM D, YYYY')}`;
}

/** The comparison window the API used: same span, immediately before `start`. */
export function getPreviousWindow(window: ReportWindow): ReportWindow {
	const days = moment(window.end).diff(moment(window.start), 'days');
	return { start: moment(window.start).subtract(days, 'days').toISOString(), end: window.start };
}

export function formatCount(value: number | null | undefined): string {
	if (value == null || Number.isNaN(value)) return '—';
	if (Math.abs(value) >= 10_000) {
		const thousands = Math.round((value / 1000) * 10) / 10;
		return `${thousands % 1 === 0 ? thousands.toFixed(0) : thousands.toFixed(1)}K`;
	}
	return value.toLocaleString('en-US');
}

export function formatDurationMinutes(minutes: number | null | undefined): string {
	if (minutes == null || Number.isNaN(minutes) || minutes < 0) return '—';
	const totalMinutes = Math.round(minutes);
	if (totalMinutes < 60) return `${totalMinutes}m`;
	if (totalMinutes < 1440) {
		const hours = Math.floor(totalMinutes / 60);
		const rest = totalMinutes % 60;
		return rest === 0 ? `${hours}h` : `${hours}h ${rest}m`;
	}
	const days = Math.floor(totalMinutes / 1440);
	const hours = Math.floor((totalMinutes % 1440) / 60);
	return hours === 0 ? `${days}d` : `${days}d ${hours}h`;
}

export function formatPercent(ratio: number | null | undefined): string {
	if (ratio == null || Number.isNaN(ratio)) return '—';
	return `${Math.round(ratio * 100)}%`;
}

/** Formats an already-percent CSAT value returned on the API's 0–100 scale. */
export function formatCsatScore(score: number | null | undefined): string {
	if (score == null || Number.isNaN(score)) return '—';
	return `${Math.round(score)}%`;
}

export function formatDayLabel(date: string | null | undefined): string {
	if (!date) return '—';
	return moment(date, 'YYYY-MM-DD').format('MMM D');
}

export type DeltaTone = 'good' | 'bad' | 'neutral';

export interface Delta {
	text: string;
	tone: DeltaTone;
	/** What the delta compares against — rendered after the text so net flow is never labeled "vs. previous". */
	hint: string;
}

/** Percent change vs. the previous period; null when there is no previous baseline to compare. */
export function getDelta(
	current: number | null | undefined,
	previous: number | null | undefined,
	lowerIsBetter = false
): Delta | null {
	if (current == null) return null;
	if (previous == null || previous === 0) return null;
	const hint = 'vs. previous';
	const percent = Math.round(((current - previous) / previous) * 100);
	if (percent === 0) return { text: 'No change', tone: 'neutral', hint };
	if (percent > 0) {
		return { text: `▲ ${percent}%`, tone: lowerIsBetter ? 'bad' : 'good', hint };
	}
	return { text: `▼ ${Math.abs(percent)}%`, tone: lowerIsBetter ? 'good' : 'bad', hint };
}

/** Absolute percentage-point change for metrics, such as CSAT, that already use a 0–100 scale. */
export function getScoreDelta(current: number | null | undefined, previous: number | null | undefined): Delta | null {
	if (current == null || previous == null) return null;
	const points = Math.round(current - previous);
	const hint = 'vs. previous';
	if (points === 0) return { text: 'No change', tone: 'neutral', hint };
	if (points > 0) return { text: `▲ ${points} pts`, tone: 'good', hint };
	return { text: `▼ ${Math.abs(points)} pts`, tone: 'bad', hint };
}

/** Backlog flow for the period: created − closed. A shrinking backlog (negative net) is good. */
export function getNetDelta(created: number, closed: number): Delta {
	const net = created - closed;
	const hint = 'net this period';
	if (net === 0) return { text: '±0', tone: 'neutral', hint };
	if (net > 0) return { text: `+${formatCount(net)}`, tone: 'bad', hint };
	return { text: `−${formatCount(Math.abs(net))}`, tone: 'good', hint };
}

// The API caps ranges at 400 days; mirrored here so a malformed window can't loop unbounded.
const DAILY_SERIES_DAYS_MAX = 400;

/** Expand the sparse daily buckets to one entry per local calendar day of the window. */
export function fillDailySeries(window: ReportWindow, buckets: ISupportReportDayCount[]): ISupportReportDayCount[] {
	const countsByDate = new Map(buckets.map(bucket => [bucket.date, bucket.count]));
	const filled: ISupportReportDayCount[] = [];
	const cursor = moment(window.start);
	const end = moment(window.end);
	while (cursor.isBefore(end) && filled.length < DAILY_SERIES_DAYS_MAX) {
		const date = cursor.format('YYYY-MM-DD');
		filled.push({ date, count: countsByDate.get(date) ?? 0 });
		cursor.add(1, 'day');
	}
	// The server's previous window is an absolute-time span, so across a DST fall-back it starts
	// an hour before this calendar-day window and can emit an edge bucket we would otherwise drop.
	// Fold out-of-window buckets into the nearest edge day so every reported count stays counted.
	if (filled.length > 0) {
		const firstDate = filled[0].date;
		const lastDate = filled[filled.length - 1].date;
		for (const bucket of buckets) {
			if (bucket.date < firstDate) filled[0].count += bucket.count;
			if (bucket.date > lastDate) filled[filled.length - 1].count += bucket.count;
		}
	}
	return filled;
}

export interface VolumeTrendPoint {
	date: string;
	label: string;
	previousDate: string | null;
	current: number;
	previous: number | null;
}

/** Overlay the previous period on the current one by day offset (index), for the trend chart. */
export function alignVolumeSeries(
	current: ISupportReportDayCount[],
	previous: ISupportReportDayCount[]
): VolumeTrendPoint[] {
	return current.map((day, index) => ({
		date: day.date,
		label: formatDayLabel(day.date),
		previousDate: previous[index]?.date ?? null,
		current: day.count,
		previous: previous[index]?.count ?? null,
	}));
}

const BUCKET_LABELS: Record<SupportReportFirstResponseBucketKey, string> = {
	lt15m: '< 15m',
	m15to30: '15–30m',
	m30to1h: '30–60m',
	h1to2: '1–2h',
	h2to4: '2–4h',
	h4plus: '4h+',
};

export function bucketLabel(key: SupportReportFirstResponseBucketKey): string {
	return BUCKET_LABELS[key];
}

export interface FirstResponseRow {
	key: SupportReportFirstResponseBucketKey;
	label: string;
	count: number;
	/** Share of all measured tickets, 0–100. */
	percent: number;
	/** Bar length relative to the largest bucket, 0–100. */
	widthPercent: number;
}

export function buildFirstResponseRows(buckets: ISupportReportFirstResponseBucket[]): FirstResponseRow[] {
	const total = buckets.reduce((sum, bucket) => sum + bucket.count, 0);
	const largest = buckets.reduce((max, bucket) => Math.max(max, bucket.count), 0);
	return buckets.map(bucket => ({
		key: bucket.key,
		label: bucketLabel(bucket.key),
		count: bucket.count,
		percent: total > 0 ? Math.round((bucket.count / total) * 100) : 0,
		widthPercent: largest > 0 ? Math.round((bucket.count / largest) * 100) : 0,
	}));
}

export const UNCATEGORIZED_LABEL = 'Uncategorized';

const CATEGORY_FALLBACK_COLORS = [
	THEME.colors.chart.levBlue,
	THEME.colors.chart.prestoPurple,
	THEME.colors.chart.tricksterGreen,
	THEME.colors.chart.magicOrange,
	THEME.colors.chart.enchantment,
	THEME.colors.chart.potionPink,
];

export interface CategoryVolumeRow {
	key: string;
	name: string;
	color: string;
	count: number;
	/** Bar length relative to the largest category, 0–100. */
	widthPercent: number;
}

/** API-resolved names/colors win (they cover foreign mailboxes); the categories query is the fallback join. */
export function buildCategoryRows(
	counts: ISupportReportCategoryCount[],
	categories: ISupportCategory[]
): CategoryVolumeRow[] {
	const categoriesById = new Map(categories.map(category => [category.id, category]));
	const largest = counts.reduce((max, row) => Math.max(max, row.count), 0);
	let fallbackIndex = 0;
	return counts.map(row => {
		const widthPercent = largest > 0 ? Math.round((row.count / largest) * 100) : 0;
		if (row.categoryId == null) {
			return {
				key: 'uncategorized',
				name: UNCATEGORIZED_LABEL,
				color: THEME.colors.chart.charmGray,
				count: row.count,
				widthPercent,
			};
		}
		const category = categoriesById.get(row.categoryId);
		const color =
			row.color || category?.color || CATEGORY_FALLBACK_COLORS[fallbackIndex++ % CATEGORY_FALLBACK_COLORS.length];
		return {
			key: row.categoryId,
			name: row.name ?? category?.name ?? 'Former category',
			color,
			count: row.count,
			widthPercent,
		};
	});
}

const TOP_AGENTS_COUNT = 5;

export function getTopAgents(agents: ISupportReportAgent[]): ISupportReportAgent[] {
	return [...agents].sort((a, b) => b.closedCount - a.closedCount).slice(0, TOP_AGENTS_COUNT);
}

export interface AssistantStats {
	draftsGenerated: string;
	acceptanceRate: string;
	coverage: string;
	averageConfidence: string;
	/** 0–100 for the "Drafted replies on N%" pill; null when nothing was created. */
	coveragePercent: number | null;
}

export function getAssistantStats(assistant: ISupportReportAssistant, createdCount: number): AssistantStats {
	const decidedCount = assistant.draftsAccepted + assistant.draftsRejected;
	const acceptance = decidedCount > 0 ? assistant.draftsAccepted / decidedCount : null;
	const coverage = createdCount > 0 ? assistant.draftedTicketCount / createdCount : null;
	return {
		draftsGenerated: formatCount(assistant.draftsGenerated),
		acceptanceRate: formatPercent(acceptance),
		coverage: formatPercent(coverage),
		averageConfidence: assistant.averageConfidence == null ? '—' : `${Math.round(assistant.averageConfidence)}%`,
		coveragePercent: coverage == null ? null : Math.round(coverage * 100),
	};
}

function escapeCsvField(value: string | number | null | undefined): string {
	let text = value == null ? '' : String(value);
	// Spreadsheet apps can execute text fields beginning with formula markers.
	if (typeof value === 'string' && /^\s*[=+\-@\t\r\n]/.test(text)) text = `'${text}`;
	if (/[",\r\n]/.test(text)) return `"${text.replaceAll('"', '""')}"`;
	return text;
}

function csvLine(...fields: Array<string | number | null | undefined>): string {
	return fields.map(escapeCsvField).join(',');
}

export function buildReportCsv(overview: ISupportReportOverview, categories: ISupportCategory[]): string {
	const window: ReportWindow = { start: overview.start, end: overview.end };
	const currentSeries = fillDailySeries(window, overview.volume.createdByDay);
	const previousSeries = fillDailySeries(getPreviousWindow(window), overview.volume.createdByDayPrevious);
	const lines = [
		csvLine('Metric', 'Current', 'Previous'),
		csvLine('Conversations created', overview.volume.created, overview.volume.createdPrevious),
		csvLine('Conversations closed', overview.volume.closed, overview.volume.closedPrevious),
		csvLine('Open backlog', overview.volume.openBacklog, null),
		csvLine('CSAT (%)', overview.csat.score, overview.csat.scorePrevious),
		csvLine('CSAT responses', overview.csat.responseCount, null),
		csvLine('Average rating', overview.csat.averageRating, null),
		csvLine(
			'Median first response (minutes)',
			overview.firstResponse.medianMinutes,
			overview.firstResponse.medianMinutesPrevious
		),
		csvLine(
			'Median resolution (minutes)',
			overview.resolution.medianMinutes,
			overview.resolution.medianMinutesPrevious
		),
		csvLine('Drafts generated', overview.assistant.draftsGenerated, null),
		csvLine('Drafts accepted', overview.assistant.draftsAccepted, null),
		csvLine('Drafts rejected', overview.assistant.draftsRejected, null),
		csvLine('Drafted conversations', overview.assistant.draftedTicketCount, null),
		csvLine('Average confidence', overview.assistant.averageConfidence, null),
		'',
		csvLine('Day', 'Created', 'Previous day', 'Created previous'),
		...currentSeries.map((day, index) =>
			csvLine(day.date, day.count, previousSeries[index]?.date, previousSeries[index]?.count)
		),
		'',
		csvLine('First response bucket', 'Count'),
		...overview.firstResponse.buckets.map(bucket => csvLine(bucketLabel(bucket.key), bucket.count)),
		'',
		csvLine('CSAT rating', 'Count'),
		...overview.csat.ratings.map(row => csvLine(`${row.rating} star`, row.count)),
		'',
		csvLine('Category', 'Conversations'),
		...buildCategoryRows(overview.categories, categories).map(row => csvLine(row.name, row.count)),
		'',
		csvLine('Agent', 'Resolved', 'Median first response (minutes)', 'First replies'),
		...overview.agents.map(agent =>
			csvLine(agent.agentName, agent.closedCount, agent.medianFirstResponseMinutes, agent.firstReplyCount)
		),
	];
	if (overview.warnings.length > 0) {
		lines.push('', csvLine('Warnings'), ...overview.warnings.map(warning => csvLine(warning)));
	}
	return lines.join('\n');
}

export function buildReportFileName(mailboxName: string | undefined, range: ReportDateRange): string {
	const slug =
		(mailboxName ?? '')
			.toLowerCase()
			.replace(/[^a-z0-9]+/g, '-')
			.replace(/^-+|-+$/g, '') || 'mailbox';
	// Reformat rather than interpolate the raw bounds: they come from the URL, and the file name
	// is written to the reader's disk.
	const start = parseReportDate(range.startDate);
	const end = parseReportDate(range.endDate);
	const window =
		start && end ? `${start.format(REPORT_DATE_FORMAT)}-to-${end.format(REPORT_DATE_FORMAT)}` : 'custom-range';
	return `support-report-${slug}-${window}.csv`;
}
